import type {Schemas} from 'domainTypes/dto';
import {ebsApi} from 'store/ebsApi';
import type {CompaniesCache} from './types';
import {mapCompaniesDto} from './mappers';

/** All product owners share the same cache entry, see CompaniesCache */
const companiesCacheKey = 'companies';

const emptyCompanies: CompaniesCache = {
  companiesById: {},
  loadedIds: [],
};

export const companiesApi = ebsApi.injectEndpoints({
  endpoints: build => ({
    getCompaniesByIds: build.query<
      CompaniesCache,
      {
        ids: string[];
      }
    >({
      keepUnusedDataFor: 30,
      queryFn: async ({ids}, _, __, fetchWithBQ) => {
        // Already cached product owners are filtered out by useCompanies
        if (ids.length === 0) {
          return {data: emptyCompanies};
        }

        const response = await fetchWithBQ({
          url: `companies`,
          params: {
            ids: ids.join(','),
          },
        });
        if (response.error) {
          return {error: response.error};
        }

        return {
          data: mapCompaniesDto(response.data as Schemas.CompanyDto[], ids),
        };
      },
      serializeQueryArgs: () => companiesCacheKey,
      merge: (cache, loaded) => {
        Object.assign(cache.companiesById, loaded.companiesById);
        cache.loadedIds.push(
          ...loaded.loadedIds.filter(id => !cache.loadedIds.includes(id)),
        );
      },
      forceRefetch: ({currentArg}) => (currentArg?.ids.length ?? 0) > 0,
    }),
  }),
});

export const {useGetCompaniesByIdsQuery, usePrefetch} = companiesApi;
